import React from 'react'

import { FormRows } from '@acorns/web-components'
import * as Sentry from '@sentry/browser'
import QueryString from 'query-string'
import { pathOr } from 'ramda'
import { v4 } from 'uuid'

import { REDIRECTION_URL_KEY, VISITOR_ID_KEY } from 'app-constants'
import trackEvent from 'utils/analytics/trackEvent'
import { setCookies } from 'utils/auth'
import { sendToSentry } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getGeneralError } from 'utils/getGeneralError'

import { generateErrorCodeMap } from '../errorCodeMap'
import { errorModal } from '../utils'
import { Link, StyledLabel } from './styled-components'

export type FormValues = {
  email: string
  firstName: string
  lastName: string
  password: string
  usResident: boolean
  partnerDisclosure?: boolean
  eSignConsent: boolean
}

declare global {
  interface Window {
    udid: string
  }
}

export const isClickId = () => {
  if (window.location.search) {
    const searchString = window.location.search.split('?')[1].toLowerCase()
    const params = QueryString.parse(searchString)
    if (params.clickid) {
      return true
    } else {
      return false
    }
  } else {
    return false
  }
}

export const mapPropsToValues = ({ isPartnerReg, partner }) => {
  // Base form values
  const values: FormValues = {
    email: partner ? partner.user.email : '',
    firstName: '',
    lastName: '',
    password: '',
    usResident: false,
    eSignConsent: false,
  }

  // If this is partner reg, add default for disclosure property
  if (isPartnerReg || isClickId()) {
    values.partnerDisclosure = false
  }

  return values
}

export const handleFormSubmit = (values, { props, ...ownProps }) => {
  const { partner } = props

  ownProps.setSubmitting(true)
  // Add Sentry breadcrumb in case there is an error
  Sentry.addBreadcrumb({
    message: 'User is attempting to create a new account.',
  })

  // Prepare data to be used in call to create user
  const user = {
    email: values.email,
    firstName: values.firstName,
    lastName: values.lastName,
    password: values.password,
  }

  const sourceAttributes = props.getSourceAttributes()
  let partnerName = null
  if (partner) {
    partnerName = partner.org.name
  } else if (isClickId()) {
    partnerName = 'impactradius'
  }

  const agreementIds = props.getAgreementIds(partnerName)

  // Provide authorization if registering through a partner
  // Default auth is the externalId and name as provided by partner
  const authorization = partner
    ? {
        authorizationUid: pathOr(partner.org.externalId, ['uid'], partner.org.authorization),
        authorizationProvider: pathOr(partner.org.name, ['provider'], partner.org.authorization),
        authorizationAccessToken: pathOr(null, ['accessToken'], partner.org.authorization),
        authorizationRefreshToken: pathOr(null, ['refreshToken'], partner.org.authorization),
        authorizationExpiresAt: pathOr(null, ['expiresAt'], partner.org.authorization),
      }
    : null

  const VISITOR_ID = document.cookie
    .split(';')
    .filter(cookie => cookie.includes(`${VISITOR_ID_KEY}`))
    .toString()
    .split('=')
    .pop()

  const data = {
    ...user,
    ...authorization,
    acceptanceDocumentVersionUuids: agreementIds,
    captchaToken: '',
    clientCredentialsToken: partner ? partner.org.clientCredentialsToken : null,
    codeGroup: props.referral ? props.referral.group : null,
    invitationCode: props.referral ? props.referral.code : null,
    sourceAttributes: sourceAttributes.attributes,
    sourceId: sourceAttributes.id,
    udid: VISITOR_ID ? VISITOR_ID : v4(),
    usResident: true,
  }

  // Create the user
  return props
    .createUser({
      variables: data,
    })
    .then(({ data: { createUser } }) => {
      const { redirectUrl, token, uuid } = createUser

      setCookies({
        identityId: uuid,
        token,
      })

      // If response contained a redirction URL, store it
      if (redirectUrl) {
        window.sessionStorage.setItem(REDIRECTION_URL_KEY, redirectUrl)
      }

      // Add token to backend provider
      props.backend.defaults.headers.Authorization = `token ${token}`

      // Identify the user for subsequent tracking in interior pages
      window.analytics.identify(uuid, {
        partner: partnerName,
        funnel_type: props.flow,
        tier_price: props.tierPrice,
        source_id: sourceAttributes.id,
      })

      // Identify the user for error tracking
      Sentry.configureScope(scope => {
        scope.setUser({ id: uuid })
      })
    })
    .then(() => {
      if (props.flow === 'spend') {
        return props
          .updateRoundUpProfile({
            variables: {
              roundUpsEnabled: false,
            },
          })
          .catch(error => {
            sendToSentry(error, {
              breadcrumb: 'Failed to disable round ups on round up profile',
            })
          })
      }
    })
    .then(() => {
      props.subscribeUserOnSignup()
    })
    .then(() => {
      trackEvent('Funnel Started', {
        eventData: {
          event: 'funnel_started',
          event_text: 'Funnel Started',
        },
        location,
        partner,
        flow: props.flow,
      })
    })
    .then(() => {
      // Update user profile data if user is registering through a partner
      /* istanbul ignore else */
      if (partner) {
        return props.updateUserProfile(user.email).then(() => {
          // Link funding source if account token is present
          if (partner.accountToken) {
            props.backend
              .post('v1/accounts', {
                account: {
                  account_token: partner.accountToken,
                },
              })
              .then(() => {
                props.route({ from: FlowRoute.Signup, action: 'continue' })
              })
              .catch(error => {
                sendToSentry(error, {
                  breadcrumb:
                    'the user is coming from a partner but v1/accounts was unable to link a funding source.',
                })
                props.route({ from: FlowRoute.Signup, action: 'continue' })
              })
          } else {
            props.route({ from: FlowRoute.Signup, action: 'continue' })
          }
        })
      }
    })
    .then(() => {
      /* istanbul ignore else */
      if (!partner) {
        props.route({ from: FlowRoute.Signup, action: 'continue' })
        ownProps.setSubmitting(false)
      }
    })
    .catch(error => {
      ownProps.setSubmitting(false)
      let errorCode

      if (error.graphQLErrors) {
        // Obtain error code from GraphQL response
        errorCode = pathOr(
          null,
          ['graphQLErrors', 0, 'extensions', 'response', 'body', 'errors', 0],
          error,
        )
      } else {
        // Obtain error from REST response
        errorCode = pathOr(null, ['response', 'data', 'errors', 0], error)
      }

      const errorCodeMap = generateErrorCodeMap(props, values)

      // This is for handling when backend sends us an error where captcha is required.
      // We don't want to rely on using a toggle to check backend because our client should
      // not be concerned with backend toggles.

      /* istanbul ignore else */
      if (errorCode && errorCode.toLowerCase().includes('captcha failed')) {
        ownProps.setSubmitting(false)
        return
      }

      if (errorCode && errorCodeMap.get(errorCode)) {
        const { title, message, actions } = errorCodeMap.get(errorCode)[0]
        props.showErrorModal(errorModal(props, title, message, actions, errorCode))
      } else {
        const retryHandler = () => {
          props.closeErrorModal()
          ownProps.submitForm()
        }
        const { title, message, actions, image } = getGeneralError(error, props, retryHandler)
        props.showErrorModal(errorModal(props, title, message, actions, errorCode, image))
      }

      sendToSentry(error, { breadcrumb: 'Attempting to register a new user' })
    })
}

const residencyLabel = openAgreementModal => (
  <StyledLabel>
    I am a U.S. Resident, 18 years or older, <strong>I have read and agree</strong> to the{' '}
    <Link onClick={openAgreementModal('program_agreement')} data-testid="program_agreement">
      Program Agreement
    </Link>
    ,{' '}
    <Link onClick={openAgreementModal('acorns_privacy_policy')} data-testid="acorns_privacy_policy">
      Privacy Policy
    </Link>
    ,{' '}
    <Link
      onClick={openAgreementModal('spend_truth_in_savings_disclosure')}
      data-testid="spend_truth_in_savings_disclosure"
    >
      Truth in Savings Disclosure
    </Link>
    ,{' '}
    <Link
      onClick={openAgreementModal('spend_terms_and_conditions')}
      data-testid="spend_terms_and_conditions"
    >
      Terms and Conditions
    </Link>
    ,{' '}
    <Link
      onClick={openAgreementModal('spend_electronic_funds_transfers_disclosure')}
      data-testid="spend_electronic_funds_transfers_disclosure"
    >
      Electronic Funds Transfers Disclosure
    </Link>
    ,{' '}
    <Link
      onClick={openAgreementModal('spend_funds_availability')}
      data-testid="spend_funds_availability"
    >
      Spend Funds Availability
    </Link>
    ,{' '}
    <Link onClick={openAgreementModal('spend_privacy_policy')} data-testid="spend_privacy_policy">
      LSB Privacy Policy
    </Link>
    ,{' '}
    <Link onClick={openAgreementModal('eft_agreement')} data-testid="eft_agreement">
      Auto Debit Authorization
    </Link>
  </StyledLabel>
)

const eSignConsentLabel = openAgreementModal => (
  <StyledLabel>
    I have read and agree to the{' '}
    <Link onClick={openAgreementModal('spend_esign_consent')} data-testid="spend_esign_consent">
      E-Sign Consent
    </Link>{' '}
  </StyledLabel>
)

const partnerDisclosureLabel = (openAgreementModal, partnerName) => {
  const agreementName = partnerName === 'PayPal' ? 'paypal_agreement' : 'partner_agreement'
  return (
    <StyledLabel>
      I have read the{' '}
      <Link onClick={openAgreementModal(agreementName)}>Partner Referral Disclosure</Link>
    </StyledLabel>
  )
}

export const identityRows: FormRows = [
  [
    {
      label: 'Legal First Name *',
      name: 'firstName',
      type: 'text',
      placeholder: 'John',
    },
    {
      label: 'Legal Last Name *',
      name: 'lastName',
      type: 'text',
      placeholder: 'Doe',
    },
  ],
]

export const disclosureRows = (openAgreementModal, partner) => {
  const rows: FormRows = [
    [
      {
        label: eSignConsentLabel(openAgreementModal),
        name: 'eSignConsent',
        sizes: { sm: 12, md: 12, xs: 12 },
        className: 'signup-checkbox esign-checkbox',
        type: 'checkbox',
      },
      {
        label: residencyLabel(openAgreementModal),
        name: 'usResident',
        sizes: { sm: 12, md: 12, xs: 12 },
        className: `signup-checkbox ${(partner || isClickId()) && 'resident-checkbox'}`,
        type: 'checkbox',
      },
    ],
  ]

  // If partner registration, add a row for the partner agreement checkbox
  if (partner || isClickId()) {
    rows.push([
      {
        label: partnerDisclosureLabel(
          openAgreementModal,
          pathOr('Impact Radius', ['org', 'name'], partner),
        ),
        name: 'partnerDisclosure',
        sizes: { sm: 12, md: 12, xs: 12 },
        className: 'signup-checkbox',
        type: 'checkbox',
      },
    ])
  }

  return rows
}
